import { useState, useEffect, useMemo, useCallback, useRef, forwardRef, useImperativeHandle, memo } from 'react';
import { useIntl } from 'react-intl';
import { Input, Upload, Button, Pagination } from 'antd';
import { PlusOutlined, DeleteOutlined, CloseOutlined } from '@ant-design/icons';
import { useGetAllfiles, useCreateFile, useDeleteFile } from 'api/fileApi';
import _, { isArray } from 'lodash';
import { Notification } from 'components/Notification';

const getIdsImage = (items, Arr) => {
  if (isArray(Arr)) _.map(items, (item) => {
    if (item?.id) Arr.push(+item?.id);
    if (item?.uid) Arr.push(+item?.uid);
  });
  return Arr;
};

const ImagePreviewModal = forwardRef(function ImagePreviewModal(_, ref) {
  const [previewSrc, setPreviewSrc] = useState('');

  useImperativeHandle(
    ref,
    () => ({
      open(src) {
        setPreviewSrc(src);
      },
      close() {
        setPreviewSrc('');
      },
    }),
    [],
  );

  useEffect(() => {
    if (!previewSrc) return undefined;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') setPreviewSrc('');
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [previewSrc]);

  if (!previewSrc) return null;

  return (
    <div
      onClick={() => setPreviewSrc('')}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1050,
        backgroundColor: 'rgba(0, 0, 0, 0.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          position: 'relative',
          background: '#fff',
          borderRadius: 2,
          padding: 24,
          maxWidth: 520,
          width: '100%',
          boxShadow:
            '0 3px 6px -4px rgba(0, 0, 0, 0.12), 0 6px 16px 0 rgba(0, 0, 0, 0.08), 0 9px 28px 8px rgba(0, 0, 0, 0.05)',
        }}
      >
        <button
          type="button"
          onClick={() => setPreviewSrc('')}
          style={{
            position: 'absolute',
            top: 0,
            right: 0,
            width: 56,
            height: 56,
            border: 'none',
            background: 'transparent',
            cursor: 'pointer',
            color: 'rgba(0, 0, 0, 0.45)',
            fontSize: 16,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CloseOutlined />
        </button>
        <img
          alt=""
          src={previewSrc}
          style={{
            width: '100%',
            maxHeight: '70vh',
            objectFit: 'contain',
            display: 'block',
          }}
        />
      </div>
    </div>
  );
});

const LibraryCard = memo(function LibraryCard({
  item,
  selected,
  onToggleSelect,
  onPreview,
  onDelete,
}) {
  return (
    <div
      style={{
        border: selected ? '2px solid #1890ff' : '1px solid #d9d9d9',
        borderRadius: 4,
        overflow: 'hidden',
        cursor: 'pointer',
        position: 'relative',
        contain: 'content',
      }}
      onClick={() => onToggleSelect(item._id)}
    >
      <img
        src={item.thumbnailUrl}
        alt=""
        loading="lazy"
        decoding="async"
        style={{ width: '100%', height: 120, objectFit: 'cover', display: 'block' }}
        onClick={(e) => {
          e.stopPropagation();
          onPreview(item.thumbnailUrl);
        }}
      />
      <div
        style={{
          padding: 4,
          fontSize: 12,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {item.type || 'image'}
        </span>
        <DeleteOutlined
          style={{ color: '#ff4d4f', cursor: 'pointer' }}
          onClick={(e) => {
            e.stopPropagation();
            onDelete(item);
          }}
        />
      </div>
    </div>
  );
});

export default function Library() {
  const intl = useIntl();
  const createMutation = useCreateFile();
  const deleteMutation = useDeleteFile();
  const { data, refetch } = useGetAllfiles();

  const [searchValue, setSearchValue] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const previewModalRef = useRef(null);
  const pageSize = 20;

  const fileList = useMemo(() => {
    const rawList = data?.data;
    if (!Array.isArray(rawList) || rawList.length === 0) return [];
    const result = new Array(rawList.length);
    for (let i = 0; i < rawList.length; i += 1) {
      const item = rawList[i];
      result[i] = {
        ...item,
        _id: item.id,
        thumbnailUrl: item.path,
        _sortTime: item?.created_at ? Date.parse(item.created_at) || 0 : 0,
        _sortId: Number(item?.id) || 0,
      };
    }
    result.sort((a, b) => {
      if (a._sortTime !== b._sortTime) return b._sortTime - a._sortTime;
      return b._sortId - a._sortId;
    });
    return result;
  }, [data?.data]);

  const filteredList = useMemo(() => {
    const keyword = searchValue.trim().toLowerCase();
    if (!keyword) return fileList;
    return fileList.filter((item) => (item?.type || '').toLowerCase().includes(keyword));
  }, [fileList, searchValue]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchValue]);

  const pagedList = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return filteredList.slice(startIndex, startIndex + pageSize);
  }, [filteredList, currentPage, pageSize]);

  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);

  const handleUpload = async (options) => {
    const file = options && options.file ? options.file : options;
    const formData = new FormData();
    formData.append('file', file);
    formData.append('type', 'image');
    await createMutation.mutateAsync(
      { data: formData },
      {
        onSuccess() {
          Notification('success', intl.formatMessage({ id: 'message.createSuccess' }));
          refetch();
        },
        onError(error) {
          Notification('error', error?.message ?? intl.formatMessage({ id: 'message.commonError' }));
        },
      },
    );
    return true;
  };

  const handleDeleteOne = useCallback(
    async (item) => {
      const ids = getIdsImage([item], []);
      if (ids.length > 0) {
        await deleteMutation.mutateAsync(ids, {
          onSuccess() {
            Notification('success', intl.formatMessage({ id: 'message.deleteSuccess' }));
            refetch();
          },
          onError(error) {
            Notification('error', error?.message ?? intl.formatMessage({ id: 'message.commonError' }));
          },
        });
      }
    },
    [deleteMutation, intl, refetch],
  );

  const handleDeleteSelected = async () => {
    if (selectedIds.length === 0) return;
    await deleteMutation.mutateAsync(selectedIds, {
      onSuccess() {
        Notification('success', intl.formatMessage({ id: 'message.deleteSuccess' }));
        setSelectedIds([]);
        refetch();
      },
      onError(error) {
        Notification('error', error?.message ?? intl.formatMessage({ id: 'message.commonError' }));
      },
    });
  };

  const toggleSelect = useCallback((id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  }, []);

  const handlePreview = useCallback((url) => {
    previewModalRef.current?.open(url);
  }, []);

  return (
    <div style={{ padding: 16 }}>
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, alignItems: 'center', flexWrap: 'wrap' }}>
        <Input
          style={{ width: 260 }}
          placeholder="Search type image: image or audio or menu."
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
        />
        <Upload
          accept="image/*"
          customRequest={handleUpload}
          showUploadList={false}
        >
          <Button type="primary" icon={<PlusOutlined />}>Upload</Button>
        </Upload>
        <Button
          danger
          icon={<DeleteOutlined />}
          disabled={selectedIds.length === 0}
          onClick={handleDeleteSelected}
        >
          Delete selected ({selectedIds.length})
        </Button>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(5, 1fr)',
          gap: 12,
        }}
      >
        {pagedList.map((item) => (
          <LibraryCard
            key={item._id}
            item={item}
            selected={selectedSet.has(item._id)}
            onToggleSelect={toggleSelect}
            onPreview={handlePreview}
            onDelete={handleDeleteOne}
          />
        ))}
      </div>

      <div style={{ marginTop: 16, display: 'flex', justifyContent: 'center' }}>
        <Pagination
          current={currentPage}
          pageSize={pageSize}
          total={filteredList.length}
          onChange={(page) => setCurrentPage(page)}
          showSizeChanger={false}
        />
      </div>

      <ImagePreviewModal ref={previewModalRef} />
    </div>
  );
}
